/**
 * WhatIfPanel – Slide-out drawer for running what-if simulations.
 * Demo-mode only. All computation is in-memory — no data is saved.
 */
import { useState, useCallback } from 'react';
import { X, FlaskConical, Play, RotateCcw, ShieldAlert } from 'lucide-react';
import { WhatIfSlider } from './WhatIfSlider';
import { WhatIfResultCard } from './WhatIfResultCard';
import {
  WHAT_IF_PRESETS,
  runSimulation,
  type WhatIfPreset,
  type WhatIfSimulationResult,
  type WhatIfFieldChange,
} from '@/modules/shared/services/whatIfApiService';

interface WhatIfPanelProps {
  programId: string;
  open: boolean;
  onClose: () => void;
}

export function WhatIfPanel({ programId, open, onClose }: WhatIfPanelProps) {
  const [selectedPreset, setSelectedPreset] = useState<WhatIfPreset | null>(null);
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});
  const [result, setResult] = useState<WhatIfSimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePresetSelect = useCallback((preset: WhatIfPreset) => {
    setSelectedPreset(preset);
    const values: Record<string, number> = {};
    preset.changes.forEach((c) => {
      values[c.fieldKey] = c.value;
    });
    setSliderValues(values);
    setResult(null);
    setError(null);
  }, []);

  const handleSliderChange = useCallback((fieldKey: string, value: number) => {
    setSliderValues((prev) => ({ ...prev, [fieldKey]: value }));
  }, []);

  const handleRunSimulation = useCallback(async () => {
    if (!selectedPreset) return;
    setLoading(true);
    setError(null);
    try {
      const changes: WhatIfFieldChange[] = selectedPreset.changes.map((c) => ({
        ...c,
        value: sliderValues[c.fieldKey] ?? c.value,
      }));
      const res = await runSimulation(programId, {
        scenarioName: selectedPreset.name,
        entityType: selectedPreset.entityType,
        changes,
      });
      setResult(res);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Simulation failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [programId, selectedPreset, sliderValues]);

  const handleReset = useCallback(() => {
    setSelectedPreset(null);
    setSliderValues({});
    setResult(null);
    setError(null);
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-[380px] max-w-full bg-background border-l border-border shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-3.5 h-3.5 text-primary" />
            <span className="text-[14px] font-semibold text-foreground">What-If Simulation</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>

        {/* Safety banner */}
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
          <ShieldAlert className="w-3 h-3 text-amber-600 flex-shrink-0" />
          <span className="text-[11px] text-amber-700 dark:text-amber-400">
            Simulation only — no data will be saved
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Preset selection */}
          <div className="space-y-2">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Scenario</span>
            <div className="grid grid-cols-1 gap-2">
              {WHAT_IF_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset)}
                  className={`text-left rounded-md border p-2.5 transition-colors ${
                    selectedPreset?.id === preset.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="text-[12px] font-medium text-foreground block">{preset.name}</span>
                  <span className="text-[11px] text-muted-foreground">{preset.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Slider adjustments */}
          {selectedPreset && (
            <div className="space-y-3">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Adjustments</span>
              {selectedPreset.changes.map((change) => (
                <WhatIfSlider
                  key={change.fieldKey}
                  label={change.fieldKey.replace(/_/g, ' ')}
                  value={sliderValues[change.fieldKey] ?? change.value}
                  min={0}
                  max={change.operation.includes('PERCENT') ? 100 : 10000}
                  step={change.operation.includes('PERCENT') ? 5 : 100}
                  unit={change.operation.includes('PERCENT') ? '%' : ''}
                  onChange={(v) => handleSliderChange(change.fieldKey, v)}
                />
              ))}
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Results</span>
                <span className="text-[11px] text-muted-foreground">
                  {result.affectedEntityCount} entities evaluated
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {result.delta.map((d) => (
                  <WhatIfResultCard key={d.kpiKey} delta={d} />
                ))}
              </div>
              {result.delta.length === 0 && (
                <p className="text-[11px] text-muted-foreground text-center py-4">
                  No matching data found for this scenario.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="text-[11px] text-destructive bg-destructive/10 rounded p-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-muted/30">
          <button
            onClick={handleReset}
            disabled={!selectedPreset && !result}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
          <button
            onClick={handleRunSimulation}
            disabled={!selectedPreset || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
          >
            <Play className="w-3 h-3" />
            {loading ? 'Simulating...' : 'Run Simulation'}
          </button>
        </div>
      </div>
    </div>
  );
}
